import { searchSavedSearchQuery } from '@/services/graphql/savedSearch'
import type { SavedSearchItem } from '@/services/adapters/savedSearch.adapter'

export const SAVED_SEARCH_PAGE_SIZE = 100

export type SavedSearchFilters = {
  projectID?: { eq: string }
}

export type SavedSearchPageRequest = {
  query: string
  variables: Record<string, unknown>
}

export type SavedSearchPageFetcher = (
  request: SavedSearchPageRequest,
) => Promise<{ items: SavedSearchItem[]; nextToken: number | null }>

const hasNextToken = (nextToken: number | null | undefined) =>
  nextToken !== null && nextToken !== undefined

type SavedSearchScope = {
  userID: string | undefined
  organizationID: string | undefined
  organizationMasterID: string | undefined
  isMaster: boolean
  filters?: SavedSearchFilters
}

const savedSearchFilter = { status: { ne: 'deleted' } }

const buildSearchSavedSearchRequest = (
  filter: Record<string, unknown>,
): SavedSearchPageRequest => ({
  query: searchSavedSearchQuery,
  variables: {
    limit: SAVED_SEARCH_PAGE_SIZE,
    filter,
  },
})

export const getSavedSearchListRequests = ({
  userID,
  organizationID,
  organizationMasterID,
  isMaster,
  filters,
}: SavedSearchScope): SavedSearchPageRequest[] => {
  if (filters?.projectID?.eq) {
    return [
      buildSearchSavedSearchRequest({
        ...savedSearchFilter,
        projectID: { eq: filters.projectID.eq },
      }),
    ]
  }

  if (organizationID && !isMaster) {
    return [
      buildSearchSavedSearchRequest({
        ...savedSearchFilter,
        organizationID: { eq: organizationID },
      }),
    ]
  }

  if (!isMaster && !organizationID && userID) {
    return [
      buildSearchSavedSearchRequest({
        ...savedSearchFilter,
        byUserID: { eq: userID },
      }),
    ]
  }

  if (isMaster) {
    const masterScopeID = organizationMasterID ?? organizationID
    const scopeFilters = [
      ...(organizationID ? [{ organizationID: { eq: organizationID } }] : []),
      ...(masterScopeID
        ? [{ organizationMasterID: { eq: masterScopeID } }]
        : []),
    ]

    if (scopeFilters.length === 0) return []

    return [
      buildSearchSavedSearchRequest({
        and: [
          savedSearchFilter,
          {
            or: scopeFilters,
          },
        ],
      }),
    ]
  }

  return []
}

export const fetchAllSavedSearchPages = async ({
  request,
  fetchPage,
}: {
  request: SavedSearchPageRequest
  fetchPage: SavedSearchPageFetcher
}) => {
  let nextToken: number | null = null
  const items: SavedSearchItem[] = []

  do {
    const page = await fetchPage({
      query: request.query,
      variables: {
        ...request.variables,
        nextToken,
      },
    })
    items.push(...page.items)
    nextToken = page.nextToken
  } while (hasNextToken(nextToken))

  return items
}

export const listSavedSearchItemsWithFetcher = async ({
  fetchPage,
  ...scope
}: SavedSearchScope & {
  fetchPage: SavedSearchPageFetcher
}) => {
  try {
    const requests = getSavedSearchListRequests(scope)

    if (requests.length === 0) {
      return []
    }

    return fetchAllSavedSearchPages({
      request: requests[0],
      fetchPage,
    })
  } catch (error) {
    console.error('error fetching saved searches', error)
    throw error
  }
}
